import { useEffect, useReducer, useRef } from "react";
import { useLeague } from "./useLeague";
import { ApiError, handleUnauthorizedResponse } from "../utils/apiClient";

interface State<T> {
  data?: T;
  error?: Error;
}

type Cache<T> = { [url: string]: T };

// discriminated union type
type Action<T> =
  | { type: "loading" }
  | { type: "fetched"; payload: T }
  | { type: "error"; payload: Error };

export function useRequest<T = unknown>(
  url?: string,
  token?: string,
  options?: RequestInit,
  skipLeagueHeader?: boolean,
): State<T> {
  // Handle case where LeagueContext might not be available yet (e.g., in LeagueProvider itself)
  const league = useLeague();
  const currentLeague = league?.currentLeague;
  const cache = useRef<Cache<T>>({});

  // Used to prevent state update if the component is unmounted
  const cancelRequest = useRef<boolean>(false);

  const initialState: State<T> = {
    error: undefined,
    data: undefined,
  };

  // Keep state logic separated
  const fetchReducer = (state: State<T>, action: Action<T>): State<T> => {
    switch (action.type) {
      case "loading":
        return { ...initialState };
      case "fetched":
        return { ...initialState, data: action.payload };
      case "error":
        return { ...initialState, error: action.payload };
      default:
        return state;
    }
  };

  const [state, dispatch] = useReducer(fetchReducer, initialState);

  useEffect(() => {
    // Do nothing if the url is not given
    if (!url) return;

    cancelRequest.current = false;

    const fetchData = async () => {
      dispatch({ type: "loading" });

      // If a cache exists for this url, return it
      if (cache.current[url]) {
        dispatch({ type: "fetched", payload: cache.current[url] });
        return;
      }

      try {
        let fetchOptions = {
          ...options,
          headers: {
            ...options?.headers,
            "Content-Type": "application/json",
            ...(token ? { Authorization: token } : {}),
            ...(!skipLeagueHeader && currentLeague?.abbr
              ? { "App-League": currentLeague.abbr }
              : {}),
          },
        };
        const response = await fetch(url, fetchOptions);
        if (!response.ok) {
          // Handle 401 Unauthorized - session timeout
          handleUnauthorizedResponse(response.status);
          throw new ApiError(response.status, response.statusText);
        }

        const data = (await response.json()) as T;
        cache.current[url] = data;
        if (cancelRequest.current) return;

        dispatch({ type: "fetched", payload: data });
      } catch (error) {
        if (cancelRequest.current) return;

        dispatch({ type: "error", payload: error as Error });
      }
    };

    void fetchData();

    // Use the cleanup function for avoiding a possibly...
    // ...state update after the component was unmounted
    return () => {
      cancelRequest.current = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [url, skipLeagueHeader]);

  return state;
}
